import { api, ApiError } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import type {
  TenantBankAccount,
  TenantBankAccountPayload,
  TenantSettingsPatch,
  TenantSettingsResponse,
} from './types'

async function uploadCompanyLogo(file: File): Promise<string> {
  const fd = new FormData()
  fd.append('image', file)
  const doUpload = async () => {
    const token = tokenManager.get()
    return fetch('/api/profile/company-logo', {
      method: 'POST',
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    })
  }
  let res = await doUpload()
  if (res.status === 401) {
    if (await tokenManager.refreshNow()) res = await doUpload()
  }
  if (!res.ok) {
    let body: unknown
    try { body = await res.json() } catch { /* ignore */ }
    throw new ApiError(`Logo upload failed (${res.status})`, res.status, body)
  }
  const { imageUrl } = (await res.json()) as { imageUrl: string }
  return imageUrl
}

export const companyApi = {
  get: () => api.get<TenantSettingsResponse>('/api/tenant/settings'),
  patch: (patch: TenantSettingsPatch) =>
    api.patch<TenantSettingsResponse>('/api/tenant/settings', patch),
  uploadLogo: async (file: File): Promise<string> => {
    const imageUrl = await uploadCompanyLogo(file)
    await api.patch<TenantSettingsResponse>('/api/tenant/settings', { logo_url: imageUrl })
    return imageUrl
  },

  listBankAccounts: () =>
    api.get<{ bank_accounts: TenantBankAccount[] }>('/api/tenant/bank-accounts'),
  createBankAccount: (body: TenantBankAccountPayload) =>
    api.post<{ bank_account: TenantBankAccount }>('/api/tenant/bank-accounts', body),
  updateBankAccount: (id: number, body: TenantBankAccountPayload) =>
    api.put<{ bank_account: TenantBankAccount }>(`/api/tenant/bank-accounts/${id}`, body),
  deleteBankAccount: (id: number) =>
    api.del<{ deleted: boolean }>(`/api/tenant/bank-accounts/${id}`),

  // Posting config (manual vs auto per source). Defaults: schedules =
  // auto, everything else = manual. Read by every page that posts to
  // the GL - invoices/bills auto-post when their flag is 'auto', queue
  // for the Post Now banner otherwise.
  getPostingConfig: () =>
    api.get<{ config: PostingConfig }>('/api/tenant/posting-config'),
  patchPostingConfig: (patch: Partial<PostingConfig>) =>
    api.patch<{ config: PostingConfig }>('/api/tenant/posting-config', patch),
}

export type PostingMode = 'manual' | 'auto'

export interface PostingConfig {
  bank: PostingMode
  accruals: PostingMode
  journal: PostingMode
  depreciation: PostingMode
  prepaid: PostingMode
  loan: PostingMode
  invoices: PostingMode
  bills: PostingMode
  updatedAt?: string | null
}

export const POSTING_SOURCE_LABELS: Record<keyof Omit<PostingConfig, 'updatedAt'>, string> = {
  bank: 'Bank statement entries',
  accruals: 'Accruals',
  journal: 'Journal entries',
  depreciation: 'Depreciation schedules',
  prepaid: 'Prepaid amortization schedules',
  loan: 'Loan amortization schedules',
  invoices: 'Customer invoices (AR)',
  bills: 'Vendor bills (AP)',
}
